import type { DefectVisualizationColorScheme } from '../contracts/availableLayers';
import type { DefectRule } from '../contracts/defectRulesets';

/* Band slugs on features and rules, low -> high severity. */
export const DEFECT_BAND_SLUGS = [
  'very-low',
  'low',
  'medium',
  'high',
  'very-high',
] as const;

export type DefectBandSlug = (typeof DEFECT_BAND_SLUGS)[number];

export const DEFECT_BAND_LABELS: Record<DefectBandSlug, string> = {
  'very-low': 'Very Low',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  'very-high': 'Very High',
};

export interface DefectBandLegendItem {
  readonly slug: DefectBandSlug;
  readonly label: string;
  readonly color: string;
}

export function buildBandLegendItems(
  colorScheme: DefectVisualizationColorScheme,
  labels: Partial<Record<DefectBandSlug, string>> = DEFECT_BAND_LABELS,
): readonly DefectBandLegendItem[] {
  return DEFECT_BAND_SLUGS.map((slug, index) => ({
    slug,
    label: labels[slug] ?? DEFECT_BAND_LABELS[slug],
    color: interpolateColorScheme(colorScheme, index),
  }));
}

interface Rgb {
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

function parseHexColor(hex: string): Rgb {
  const normalized = hex.trim().replace(/^#/, '');
  if (normalized.length !== 6 || !/^[0-9A-Fa-f]{6}$/.test(normalized)) {
    throw new Error(`Invalid hex colour: ${hex}`);
  }
  return {
    r: Number.parseInt(normalized.slice(0, 2), 16),
    g: Number.parseInt(normalized.slice(2, 4), 16),
    b: Number.parseInt(normalized.slice(4, 6), 16),
  };
}

function toHexColor({ r, g, b }: Rgb): string {
  const clamp = (channel: number) => Math.max(0, Math.min(255, Math.round(channel)));
  return `#${[clamp(r), clamp(g), clamp(b)]
    .map((channel) => channel.toString(16).padStart(2, '0'))
    .join('')}`;
}

function lerpRgb(start: Rgb, end: Rgb, t: number): Rgb {
  return {
    r: start.r + (end.r - start.r) * t,
    g: start.g + (end.g - start.g) * t,
    b: start.b + (end.b - start.b) * t,
  };
}

/* Interpolate between colorScheme.min (very-low) and colorScheme.max (very-high). */
export function interpolateColorScheme(
  colorScheme: DefectVisualizationColorScheme,
  bandIndex: number,
  bandCount: number = DEFECT_BAND_SLUGS.length,
): string {
  if (bandCount <= 1) {
    return colorScheme.min;
  }
  const start = parseHexColor(colorScheme.min);
  const end = parseHexColor(colorScheme.max);
  const t = bandIndex / (bandCount - 1);
  return toHexColor(lerpRgb(start, end, t));
}

/*
  Builds the five band rules used for map symbology and wizard previews.
  very-low uses colorScheme.min; very-high uses colorScheme.max.
 */
export function buildRulesFromColorScheme(
  colorScheme: DefectVisualizationColorScheme,
  labels: Partial<Record<DefectBandSlug, string>> = DEFECT_BAND_LABELS,
): readonly DefectRule[] {
  const bandCount = DEFECT_BAND_SLUGS.length;

  return DEFECT_BAND_SLUGS.map((slug, index) => ({
    id: `band-${slug}`,
    slug,
    name: labels[slug] ?? DEFECT_BAND_LABELS[slug],
    colour: interpolateColorScheme(colorScheme, index, bandCount),
    minValue: 0,
    maxValue: 99.99,
    order: index,
  }));
}
